/**
 * CorrectieMutationDialog — Handmatige potje-correctie voor eigenaren.
 * Voor uitzonderlijke gevallen (foutje, extra afspraak) buiten de reguliere
 * NMBRS-export flow.
 */

import { useState } from 'react';
import { apiFetch } from '../../stores/workforce-store';

interface Props {
  open: boolean;
  staffId: string;
  staffName: string;
  onClose: () => void;
  onSaved: () => void;
}

export function CorrectieMutationDialog({ open, staffId, staffName, onClose, onSaved }: Props) {
  const [hours, setHours] = useState<string>('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const handleSave = async () => {
    setError(null);
    const parsed = Number(hours);
    if (!Number.isFinite(parsed) || parsed === 0) {
      setError('Vul een waarde in (positief = storten, negatief = opnemen).');
      return;
    }
    if (reason.trim().length < 10) {
      setError('Reden is verplicht (min 10 tekens).');
      return;
    }
    setSaving(true);
    try {
      await apiFetch('/api/workforce/urensaldo-mutations', {
        method: 'POST',
        body: JSON.stringify({ staff_id: staffId, hours: parsed, reason: reason.trim() }),
      });
      onSaved();
      setHours('');
      setReason('');
      onClose();
    } catch (e) {
      setError((e as Error).message || 'Opslaan mislukt.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        <h2 className="text-lg font-heading font-semibold text-main">Potje-correctie</h2>
        <p className="text-xs text-muted">{staffName}</p>

        <div className="mt-4 space-y-3">
          <label className="block text-sm">
            <span className="text-xs font-medium text-muted">Uren (+ storten, − opnemen)</span>
            <input
              type="number"
              step="0.25"
              value={hours}
              onChange={e => setHours(e.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-mono"
              placeholder="bv. -2.5 of 3"
            />
          </label>

          <label className="block text-sm">
            <span className="text-xs font-medium text-muted">Reden (verplicht)</span>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={3}
              className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              placeholder="Leg uit waarom deze correctie nodig is (min 10 tekens)"
            />
          </label>

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? 'Opslaan...' : 'Correctie toevoegen'}
          </button>
        </div>
      </div>
    </div>
  );
}
